/* Wishlist storage and rendering. Two lists live side by side in
   localStorage; each holds product handles only, so nothing personal is kept
   and the data survives a page reload without an account. */
(function () {
  const KEYS = { wishlist: 'ld:wishlist', later: 'ld:saved-for-later' };

  const read = (list) => {
    try {
      const raw = localStorage.getItem(KEYS[list]);
      return raw ? JSON.parse(raw).filter((h) => typeof h === 'string') : [];
    } catch (e) {
      return [];
    }
  };

  const write = (list, handles) => {
    try {
      localStorage.setItem(KEYS[list], JSON.stringify([...new Set(handles)]));
    } catch (e) { /* storage full or blocked, list simply will not persist */ }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list } }));
  };

  const has = (list, handle) => read(list).includes(handle);

  const toggle = (list, handle) => {
    const current = read(list);
    const next = current.includes(handle) ? current.filter((h) => h !== handle) : [...current, handle];
    write(list, next);
    return next.includes(handle);
  };

  window.BrandWishlist = { read, write, has, toggle, KEYS };

  class Wishlist extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.note = this.querySelector('[data-wishlist-note]');
      this.list = 'wishlist';
      this.money = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      });

      this.querySelectorAll('[data-wishlist-tab]').forEach((btn) => {
        btn.addEventListener('click', () => this.selectTab(btn.dataset.wishlistTab));
      });

      const share = this.querySelector('[data-wishlist-share]');
      if (share) share.addEventListener('click', () => this.share());

      document.addEventListener('wishlist:change', () => this.render());

      this.adoptSharedList();
      this.render();
    }

    /* A shared link carries handles in the query string. Merge them in once so
       the recipient sees the list without overwriting anything of their own. */
    adoptSharedList() {
      const shared = new URLSearchParams(location.search).get('items');
      if (!shared) return;
      const incoming = shared.split(',').map((h) => h.trim()).filter(Boolean);
      if (incoming.length) write('wishlist', [...read('wishlist'), ...incoming]);
    }

    selectTab(list) {
      this.list = list;
      this.querySelectorAll('[data-wishlist-tab]').forEach((btn) => {
        const on = btn.dataset.wishlistTab === list;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.render();
    }

    async render() {
      this.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        const n = read(el.dataset.wishlistCount).length;
        el.textContent = n ? `(${n})` : '';
      });

      const handles = read(this.list);
      if (!handles.length) {
        this.grid.hidden = true;
        this.grid.innerHTML = '';
        this.empty.hidden = false;
        return;
      }

      const root = this.dataset.root || '/';
      const products = await Promise.all(
        handles.map((h) =>
          fetch(`${root}products/${h}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      );

      const live = products.filter(Boolean);
      /* Drop handles that no longer resolve, so a deleted product cannot leave
         a permanently broken row behind. */
      if (live.length !== handles.length) {
        write(this.list, live.map((p) => p.handle));
        return;
      }

      this.empty.hidden = true;
      this.grid.hidden = false;
      this.grid.innerHTML = live.map((p) => this.card(p)).join('');
      this.bindCards();
    }

    card(p) {
      const img = p.featured_image ? `${p.featured_image}&width=600` : '';
      const variant = p.variants.find((v) => v.available) || p.variants[0];
      const other = this.list === 'wishlist' ? 'later' : 'wishlist';
      const otherLabel = this.list === 'wishlist' ? 'Save for later' : 'Move to wishlist';
      return `
        <li class="wishlist__item">
          <a class="wishlist__media" href="${this.dataset.root}products/${p.handle}">
            ${img ? `<img src="${img}" alt="${p.title.replace(/"/g, '&quot;')}" loading="lazy">` : ''}
          </a>
          <div class="wishlist__info">
            <a class="wishlist__title" href="${this.dataset.root}products/${p.handle}">${p.title}</a>
            <span class="wishlist__price">${this.money.format(p.price / 100)}</span>
            <div class="wishlist__actions">
              <button type="button" class="button button--small" data-add="${variant ? variant.id : ''}" ${variant && variant.available ? '' : 'disabled'}>
                ${variant && variant.available ? 'Add to cart' : 'Sold out'}
              </button>
              <button type="button" class="wishlist__link" data-move="${p.handle}" data-target="${other}">${otherLabel}</button>
              <button type="button" class="wishlist__link" data-remove="${p.handle}">Remove</button>
            </div>
          </div>
        </li>`;
    }

    bindCards() {
      this.grid.querySelectorAll('[data-remove]').forEach((b) =>
        b.addEventListener('click', () => {
          write(this.list, read(this.list).filter((h) => h !== b.dataset.remove));
        })
      );
      this.grid.querySelectorAll('[data-move]').forEach((b) =>
        b.addEventListener('click', () => {
          const handle = b.dataset.move;
          const target = b.dataset.target;
          write(target, [...read(target), handle]);
          write(this.list, read(this.list).filter((h) => h !== handle));
        })
      );
      this.grid.querySelectorAll('[data-add]').forEach((b) =>
        b.addEventListener('click', async () => {
          if (!b.dataset.add) return;
          b.disabled = true;
          try {
            await fetch(`${this.dataset.root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: Number(b.dataset.add), quantity: 1 }] }),
            });
            location.href = `${this.dataset.root}cart`;
          } finally {
            b.disabled = false;
          }
        })
      );
    }

    async share() {
      const handles = read(this.list);
      if (!handles.length) return this.say('Add something first.');
      const url = `${location.origin}${location.pathname}?items=${handles.join(',')}`;
      try {
        await navigator.clipboard.writeText(url);
        this.say('Link copied.');
      } catch (e) {
        this.say(url);
      }
    }

    say(msg) {
      if (!this.note) return;
      this.note.textContent = msg;
      setTimeout(() => { this.note.textContent = ''; }, 4000);
    }
  }

  if (!customElements.get('brand-wishlist')) customElements.define('brand-wishlist', Wishlist);
})();

/* Heart toggles anywhere on the site. Bound on load and after any section
   re-render, since Dawn swaps card markup when facets or variants change. */
(function () {
  const sync = (btn) => {
    const on = window.BrandWishlist.has('wishlist', btn.dataset.wishlistToggle);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  };

  const bind = () => {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      sync(btn);
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.BrandWishlist.toggle('wishlist', btn.dataset.wishlistToggle);
        sync(btn);
      });
    });
  };

  document.addEventListener('DOMContentLoaded', bind);
  document.addEventListener('wishlist:change', bind);
  new MutationObserver(bind).observe(document.body, { childList: true, subtree: true });
  bind();
})();
